import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDays, addHours, isAfter, isEqual, startOfDay } from 'date-fns';

import Claim from '@/apps/mwd-v2/components/Domain/Claim/Claim';
import Inzet from '@/apps/mwd-v2/components/Domain/Inzet/Inzet';
import { deeltrajectOnContextMenu } from '@/apps/mwd-v2/state/actions/helpers/actionStore';
import useTooltipActions from '@/apps/mwd-v2/state/actions/useTooltipActions';
import { MousePointer } from '@/apps/mwd-v2/state/types/ActionStore';
import { MwdDeeltraject } from '@/apps/mwd-v2/types/Domain';
import {
  DrgpntPositionY,
  getVariant,
  label,
  labelPositionX,
  labelPositionY,
  vertrekStationLabelX,
} from '@/apps/mwd-v2/utils/deeltraject';
import { preventDefault } from '@/apps/mwd-v2/utils/mwdEvents/eventHelpers';
import { getInzetPositie, isDeeltrajectClaim } from '@/apps/mwd-v2/utils/werklijnMeta';
import AcceptConflictBold from '@/assets/icons/AcceptConflictBold';
import Alert from '@/assets/icons/Alert';
import ChatBalloon from '@/assets/icons/ChatBalloon';
import useClientState from '@/state/useClientState';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { Text } from '@visx/text';

import styles from '../MwdChart.module.css';
import domainStyles from '../styles/domainStyles.module.css';

type DeeltrajectProps = {
  deeltraject: MwdDeeltraject;
  isAllowedToMutate: boolean;
  miniMwd?: boolean;
};

export const deeltrajectIconStyles = {
  primary: {
    color: 'var(--tractie-color-content-default-inverse)',
    fontSize: '0.875rem',
    fontWeight: 700,
    fill: 'var(--tractie-color-content-default-inverse)',
  },
  claim: {
    color: 'var(--tractie-color-content-default)',
    fontSize: '0.875rem',
    fontWeight: 700,
    fill: 'var(--tractie-color-content-default)',
  },
  defectInzetbaar: {
    color: 'var(--mwd-color-treintaak-text-defect-inzetbaar)',
    fontSize: '0.875rem',
    fontWeight: 700,
    fill: 'var(--mwd-color-treintaak-text-defect-inzetbaar)',
  },
  korteInzet: {
    color: 'var(--tractie-color-content-subtle)',
    fontSize: '0.875rem',
    fontWeight: 700,
    fill: 'var(--tractie-color-content-subtle)',
  },
};

const Deeltraject: FC<DeeltrajectProps> = ({ deeltraject, isAllowedToMutate, miniMwd }) => {
  const { setShowDeeltrajectTooltip, hideTooltipWithTimeout } = useTooltipActions();
  const setShowContextMenu = useClientState((state) => state.setShowContextMenu);
  const setShowDetailPanel = useClientState((state) => state.setShowDetailPanel);
  const selectDeeltraject = useClientState((state) => state.selectDeeltraject);
  const hideDetailPanel = useClientState((state) => state.hideDetailPanel);
  const hideMaterieelPanel = useClientState((state) => state.hideMaterieelPanel);
  const isSelected = useClientState((state) =>
    state.selected.deeltrajecten.some((deel) => deel.wleId === deeltraject.wleId),
  );

  const isSecondDay = useMemo(() => {
    const now = new Date();
    const threshold = addHours(addDays(startOfDay(now), 2), 2); // 2 days from today at 02:00
    const vertrek = new Date(deeltraject.vertrekUitvoeringsTijd);
    return isAfter(vertrek, threshold) || isEqual(vertrek, threshold);
  }, [deeltraject.vertrekUitvoeringsTijd]);

  const [labelWidths, setLabelWidths] = useState({ main: 0, notitie: 0, dienstregelpunt: 0 });

  const textLabelRef = useRef<SVGGElement>(null);
  const notitieLabelRef = useRef<SVGGElement>(null);
  const textDienstregelpuntRef = useRef<SVGGElement>(null);

  useEffect(() => {
    const measure = () => {
      if (!textLabelRef.current || !notitieLabelRef.current || !textDienstregelpuntRef.current) return;
      setLabelWidths({
        main: textLabelRef.current.getBBox().width,
        notitie: notitieLabelRef.current.getBBox().width,
        dienstregelpunt: textDienstregelpuntRef.current.getBBox().width,
      });
    };

    const id = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(id);
  }, [deeltraject.toelichting, deeltraject.bijzonderheidscode]);

  const { labelY, labelX, vertrekLabelX, drglPuntY, hasAnyConflictVertrek, hasAnyConflictAankomst } = useMemo(() => {
    const {
      plaatsConflictVan,
      plaatsConflictNaar,
      isDienstregelpuntVanInConflict,
      isDienstregelpuntNaarInConflict,
      ketenConfig,
    } = deeltraject;

    const isTurnBreak = ketenConfig.turnKetenBreak;
    // Conflict booleans
    const hasPlaatsConflictVertrek = plaatsConflictVan && !isTurnBreak;
    const hasTijdConflictVertrek = isDienstregelpuntVanInConflict && !isTurnBreak;
    const hasPlaatsConflictAankomst = plaatsConflictNaar && !isTurnBreak;
    const hasTijdConflictAankomst = isDienstregelpuntNaarInConflict && !isTurnBreak;

    const hasBothConflictsVertrek = hasPlaatsConflictVertrek && hasTijdConflictVertrek;
    const hasAnyConflictVertrek = hasPlaatsConflictVertrek || hasTijdConflictVertrek;
    const hasAnyConflictAankomst = hasPlaatsConflictAankomst || hasTijdConflictAankomst;
    return {
      labelY: labelPositionY(deeltraject),
      labelX: labelPositionX(deeltraject),
      vertrekLabelX: vertrekStationLabelX(deeltraject, labelWidths.dienstregelpunt),
      drglPuntY: DrgpntPositionY(deeltraject),
      hasAnyConflictVertrek,
      hasBothConflictsVertrek,
      hasAnyConflictAankomst,
    };
  }, [deeltraject, labelWidths.dienstregelpunt]);

  const iconLeft = useMemo(
    () => labelX + labelWidths.main + labelWidths.notitie + 8,
    [labelX, labelWidths.main, labelWidths.notitie],
  );

  const hasPerronLengteConflict = useClientState((state) => state.perronLengteConflictInzet.has(deeltraject.wleId));
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<SVGGElement, MouseEvent>) => {
      void selectDeeltraject(deeltraject, event.shiftKey, true);
      if (event.shiftKey && event.button === 0) return preventDefault(event);
      void setShowContextMenu('deeltraject', deeltrajectOnContextMenu(deeltraject), localPoint(event) as MousePointer);
      return preventDefault(event);
    },
    [deeltraject, selectDeeltraject, setShowContextMenu],
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<SVGGElement, MouseEvent>) => {
      void selectDeeltraject(deeltraject, event.shiftKey);
      const currentState = useClientState.getState();
      if (currentState.showDetailPanel && !isDeeltrajectClaim(deeltraject)) {
        setShowDetailPanel(deeltraject);
      } else if (currentState.showDetailPanel && isDeeltrajectClaim(deeltraject)) {
        hideDetailPanel();
      }
      return preventDefault(event);
    },
    [deeltraject, selectDeeltraject, setShowDetailPanel, hideDetailPanel],
  );

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent<SVGGElement, MouseEvent>) => {
      if (!isDeeltrajectClaim(deeltraject)) {
        const currentState = useClientState.getState();
        if (currentState.showMaterieelPanel && hideMaterieelPanel) {
          hideMaterieelPanel();
        }
        setShowDetailPanel(deeltraject);
      }
      return preventDefault(event);
    },
    [deeltraject, setShowDetailPanel, hideMaterieelPanel],
  );

  const contextMenuHandler = miniMwd ? undefined : handleContextMenu;

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      void setShowDeeltrajectTooltip(
        {
          x: event.clientX,
          y: event.clientY,
        } as MousePointer,
        deeltraject,
      );
    },
    [setShowDeeltrajectTooltip, deeltraject],
  );

  const handleMouseLeave = useCallback(() => hideTooltipWithTimeout(), [hideTooltipWithTimeout]);

  const aankomstStationLabelX = deeltraject.isDienstregelpuntNaarInConflict
    ? deeltraject.width + 5
    : deeltraject.width - 20;

  return (
    <Group data-testid={`deeltraject-${deeltraject.wleId}`} left={deeltraject.x}>
      <g onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
        {isDeeltrajectClaim(deeltraject) ? (
          <Claim onContextMenu={contextMenuHandler} onClick={handleClick} claim={deeltraject} isSelected={isSelected} />
        ) : (
          <Inzet
            isAllowedToMutate={isAllowedToMutate}
            onContextMenu={contextMenuHandler}
            onDoubleClick={handleDoubleClick}
            onClick={handleClick}
            inzet={deeltraject}
            isSelected={isSelected}
          />
        )}
      </g>
      <g ref={textDienstregelpuntRef}>
        <Text
          className={hasAnyConflictVertrek ? styles.werklijnLabelStyleV2InConflict : styles.werklijnLabelStyleV2}
          x={vertrekLabelX}
          y={drglPuntY}
          pointerEvents='none'
          data-testid={hasAnyConflictVertrek && `conflict-vertrek-${deeltraject.dienstregelpuntVan}`}
          fill={isSecondDay ? 'transparent' : 'var(--tractie-color-background-default)'}
        >
          {deeltraject.dienstregelpuntVan}
        </Text>
      </g>
      {hasPerronLengteConflict && !deeltraject.inzetIsKort && (
        <Alert
          y={Number(deeltraject.inzetIsKort ? deeltraject.trapHoogteIndex * 22 : deeltraject.y) - 19}
          x={Number(labelX) + labelWidths.dienstregelpunt + 4}
          width={16}
          height={16}
          className={domainStyles.alertIcon}
          pointerEvents='none'
        />
      )}
      {deeltraject.laatAankomstStationZien && !deeltraject.inzetIsKort && (
        <g>
          <rect
            x={aankomstStationLabelX}
            y={drglPuntY - 11}
            width={35}
            height={13}
            fill={isSecondDay ? 'transparent' : 'var(--tractie-color-background-default)'}
          />
          <Text
            className={styles.werklijnLabelStyleV2}
            x={aankomstStationLabelX}
            y={drglPuntY}
            pointerEvents='none'
            data-testid={hasAnyConflictAankomst && `conflict-aankomst-${deeltraject.dienstregelpuntNaar}`}
          >
            {deeltraject.dienstregelpuntNaar}
          </Text>
        </g>
      )}
      <g ref={textLabelRef} id={`wle-label-${deeltraject.wleId}`}>
        {hasPerronLengteConflict && deeltraject.inzetIsKort && (
          <Alert
            y={Number(labelY) - 13}
            x={deeltraject.toelichting ? iconLeft + 20 : iconLeft - 4}
            width={16}
            height={16}
            className={domainStyles.alertIcon}
            pointerEvents='none'
          />
        )}
        <Text
          className={label(deeltraject, deeltraject.inzetIsKort)}
          y={labelY}
          x={hasPerronLengteConflict && deeltraject.inzetIsKort ? labelX + 20 : labelX}
          pointerEvents='none'
        >
          {`${deeltraject.treinnummer}${getInzetPositie(deeltraject)}${deeltraject.versterkt ? '+' : ''}${
            deeltraject.opzending ? '=' : ''
          } `}
        </Text>
      </g>
      <g ref={notitieLabelRef}>
        <Text
          className={label(deeltraject, deeltraject.inzetIsKort)}
          y={labelY}
          x={labelX + labelWidths.main + 4}
          pointerEvents='none'
          style={{ fontWeight: 400 }}
          data-testid={deeltraject.bijzonderheidscode && `wle-label-bijzonderheidcode-${deeltraject.wleId}`}
        >
          {deeltraject.bijzonderheidscode ?? ''}
        </Text>
      </g>
      {deeltraject.toelichting && (
        <ChatBalloon
          data-icon='ChatBalloon'
          height={16}
          width={16}
          fill='var(--tractie-color-content-default-inverse)'
          y={labelY - 12}
          x={iconLeft}
          style={deeltrajectIconStyles[getVariant(deeltraject, deeltraject.inzetIsKort)]}
        />
      )}
      {deeltraject.geaccepteerdConflict && (
        <AcceptConflictBold
          data-icon='AcceptConflictBold'
          height={16}
          width={16}
          fill='var(--tractie-color-content-default-inverse)'
          y={labelY - 13}
          x={deeltraject.toelichting ? iconLeft + 20 : iconLeft - 4}
          style={deeltrajectIconStyles[getVariant(deeltraject, deeltraject.inzetIsKort)]}
        />
      )}
    </Group>
  );
};

export default memo(Deeltraject);
